import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Box,
  Button,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import { FaGoogle } from "react-icons/fa";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import { uploadImageToGhl } from "hook/services";
import toast from "react-hot-toast";
import ReactCropModal from "layouts/super-admin/settings/Configurator/components/ReactCropModal/ReactCropModal";

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name: A to Z" },
  { value: "name_desc", label: "Name: Z to A" },
  { value: "size_asc", label: "Size: Smallest First" },
  { value: "size_desc", label: "Size: Largest First" },
  { value: "modified_desc", label: "Modified: Newest First" },
  { value: "modified_asc", label: "Modified: Oldest First" },
];

const SORT_MAP = {
  name_asc: { sortBy: "name", sortOrder: "asc" },
  name_desc: { sortBy: "name", sortOrder: "desc" },
  size_asc: { sortBy: "size", sortOrder: "asc" },
  size_desc: { sortBy: "size", sortOrder: "desc" },
  modified_asc: { sortBy: "updatedAt", sortOrder: "asc" },
  modified_desc: { sortBy: "updatedAt", sortOrder: "desc" },
};

const deriveName = (item, fallbackIdx = 0) => {
  if (item?.name) return item.name.replace(/\.[^.]+$/, "");
  const url = item?.image || "";
  try {
    const path = url.split("?")[0];
    const last = path.split("/").pop() || "";
    const stripped = last.replace(/\.[^.]+$/, "");
    if (stripped) return stripped;
  } catch (e) {}
  return `file_${fallbackIdx + 1}`;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "—";
  }
};

const formatRelativeDate = (d) => {
  if (!d) return "";
  try {
    const diffMs = Date.now() - new Date(d).getTime();
    if (diffMs < 0) return formatDate(d);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const month = 30 * day;
    const year = 365 * day;
    if (diffMs < minute) return "Just now";
    if (diffMs < hour) {
      const m = Math.floor(diffMs / minute);
      return `${m} minute${m > 1 ? "s" : ""} ago`;
    }
    if (diffMs < day) {
      const h = Math.floor(diffMs / hour);
      return `${h} hour${h > 1 ? "s" : ""} ago`;
    }
    if (diffMs < week) {
      const dd = Math.floor(diffMs / day);
      return `${dd} day${dd > 1 ? "s" : ""} ago`;
    }
    if (diffMs < month) {
      const w = Math.floor(diffMs / week);
      return `${w} week${w > 1 ? "s" : ""} ago`;
    }
    if (diffMs < year) {
      const mo = Math.floor(diffMs / month);
      return `${mo} month${mo > 1 ? "s" : ""} ago`;
    }
    const y = Math.floor(diffMs / year);
    return `${y} year${y > 1 ? "s" : ""} ago`;
  } catch (e) {
    return "";
  }
};

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
};

function GhlMediaGallery({
  open,
  onClose,
  onSelect,
  title = "Media Storage",
  confirmLabel = "Use Selected",
  multiSelect = false,
  pageSize = 20,
  showUpload = true,
  showSearch = true,
  showSort = true,
  showFilter = true,
  showViewToggle = true,
  showFilesLabel = true,
  filterOptions = [{ value: "all", label: "All" }],
  defaultSort = "modified_desc",
  defaultView = "grid",
  accept = "image/*",
  enableCrop = false,
  cropAspectRatio = null,
  googlePlaceId = "",
}) {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(defaultSort);
  const [filter, setFilter] = useState(filterOptions?.[0]?.value || "all");
  const [view, setView] = useState(defaultView);
  const [dimensions, setDimensions] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [cropImageUrl, setCropImageUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [source, setSource] = useState("ghl"); // "ghl" | "google"
  const [googleImages, setGoogleImages] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  const googleEnabled = Boolean(googlePlaceId);

  const fetchMedia = async (opts = {}) => {
    if (!agency?._id) return;
    const {
      page: targetPage = page,
      sort: sortKey = sort,
      search: searchVal = debouncedSearch,
    } = opts;
    const { sortBy, sortOrder } =
      SORT_MAP[sortKey] || SORT_MAP.modified_desc;
    const offset = (Math.max(1, targetPage) - 1) * pageSize;
    const parts = [
      `agency_id=${agency._id}`,
      `offset=${offset}`,
      `limit=${pageSize}`,
      `sortBy=${encodeURIComponent(sortBy)}`,
      `sortOrder=${encodeURIComponent(sortOrder)}`,
    ];
    if (searchVal) parts.push(`query=${encodeURIComponent(searchVal)}`);

    setLoading(true);
    try {
      const { response } = await AppService.agency.GetGhlMedia({
        query: parts.join("&"),
      });
      if (response?.success) {
        const list = response.data || [];
        setImages(list);
        if (typeof response.total === "number") {
          setTotalCount(response.total);
        } else if (list.length < pageSize) {
          setTotalCount(offset + list.length);
        } else {
          setTotalCount(offset + list.length + 1);
        }
      } else {
        toast.error(response?.message || "Failed to load media");
        setImages([]);
        setTotalCount(0);
      }
    } catch (e) {
      toast.error("Failed to load media");
      setImages([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleImages = async () => {
    if (!googlePlaceId) {
      setGoogleImages([]);
      setGoogleError(null);
      return;
    }
    if (!agency?.google_image_api_key) {
      setGoogleImages([]);
      setGoogleError(
        "Google image API key is missing. Add it under Integrations."
      );
      toast.error("Google image API key is missing");
      return;
    }
    setGoogleLoading(true);
    setGoogleError(null);
    try {
      const { response, error } = await AppService.reviews.GetGoogleImages({
        query: `place_id=${googlePlaceId}&Apikey=${agency.google_image_api_key}`,
      });
      if (error || response?.success === false) {
        const msg =
          error?.message ||
          response?.message ||
          "Failed to load Google images";
        setGoogleError(msg);
        toast.error(msg);
        setGoogleImages([]);
        return;
      }
      const urls = (response?.data || []).filter(Boolean);
      setGoogleImages(urls.map((u) => ({ image: u })));
    } catch (e) {
      const msg = e?.message || "Failed to load Google images";
      setGoogleError(msg);
      toast.error(msg);
      setGoogleImages([]);
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedImage(null);
      setSelectedImages([]);
      setSearch("");
      setDebouncedSearch("");
      setPage(1);
      setSort(defaultSort);
      setFilter(filterOptions?.[0]?.value || "all");
      setView(defaultView);
      setMenuAnchor(null);
      setMenuItem(null);
      setSource("ghl");
      setGoogleImages([]);
      setGoogleError(null);
      setImages([]);
      setTotalCount(0);
    }
  }, [open, agency?._id]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open || source !== "ghl") return;
    fetchMedia({ page, sort, search: debouncedSearch });
  }, [open, source, page, sort, debouncedSearch, agency?._id, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && source === "google" && googleEnabled && googleImages.length === 0) {
      fetchGoogleImages();
    }
  }, [open, source, googlePlaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection + paging when switching source so the views don't get crossed up
  useEffect(() => {
    setSelectedImage(null);
    setSelectedImages([]);
    setPage(1);
    setSearch("");
    setDebouncedSearch("");
  }, [source]);

  const toggleSelection = (url, _id) => {
    if (multiSelect) {
      setSelectedImages((prev) => {
      const alreadyExists = prev.some((item) => item._id === _id);

      if (alreadyExists) {
        return prev.filter((item) => item._id !== _id);
      }

      return [...prev, { url, _id }];
    });
    } else {
      setSelectedImage({url, _id});
    }
  };

  const isSelected = (url) =>
    multiSelect ? selectedImages.some((item) => item.url === url) : selectedImage?.url === url;

  const selectionCount = multiSelect ? selectedImages.length : selectedImage ? 1 : 0;

  const isGhl = source === "ghl";

  const filtered = useMemo(() => {
    if (isGhl) return images;
    let list = googleImages.slice();
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((it) => {
        const name = (it?.name || it?.image || "").toLowerCase();
        return name.includes(q);
      });
    }
    return list;
  }, [isGhl, images, googleImages, search]);

  const sorted = useMemo(() => {
    if (isGhl) return filtered;
    const list = filtered.slice();
    switch (sort) {
      case "name_asc":
        return list.sort((a, b) =>
          deriveName(a).localeCompare(deriveName(b))
        );
      case "name_desc":
        return list.sort((a, b) =>
          deriveName(b).localeCompare(deriveName(a))
        );
      case "size_asc":
        return list.sort((a, b) => (a?.size || 0) - (b?.size || 0));
      case "size_desc":
        return list.sort((a, b) => (b?.size || 0) - (a?.size || 0));
      case "modified_asc":
        return list.sort(
          (a, b) =>
            new Date(a?.dateUpdated || a?.dateAdded || 0).getTime() -
            new Date(b?.dateUpdated || b?.dateAdded || 0).getTime()
        );
      case "modified_desc":
      default:
        return list.sort(
          (a, b) =>
            new Date(b?.dateUpdated || b?.dateAdded || 0).getTime() -
            new Date(a?.dateUpdated || a?.dateAdded || 0).getTime()
        );
    }
  }, [isGhl, filtered, sort]);

  const totalItems = isGhl ? totalCount : sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sort, filter]);

  const paginated = useMemo(() => {
    if (isGhl) return sorted;
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [isGhl, sorted, safePage, pageSize]);

  // Lazy-load natural dimensions for visible items
  useEffect(() => {
    paginated.forEach((item) => {
      const url = item?.image;
      if (!url || dimensions[url]) return;
      const img = new Image();
      img.onload = () => {
        setDimensions((prev) =>
          prev[url]
            ? prev
            : { ...prev, [url]: { w: img.naturalWidth, h: img.naturalHeight } }
        );
      };
      img.src = url;
    });
  }, [paginated]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadExternalUrlToGhl = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = (blob.type && blob.type.split("/")[1]) || "jpg";
      const file = new File([blob], `google-${Date.now()}.${ext}`, {
        type: blob.type || "image/jpeg",
      });
      const result = await uploadImageToGhl(file, agency);
      if (result?.success) return { url: result.data, _id: null };
      return null;
    } catch (e) {
      console.error("Google→GHL upload failed:", e);
      return null;
    }
  };
  const handleConfirm = async () => {
    const picks = multiSelect
      ? selectedImages
      : selectedImage
      ? [selectedImage]
      : [];

    if (!picks.length) {
      toast.error(
        multiSelect
          ? "Please select at least one image"
          : "Please select an image first"
      );
      return;
    }

    if (source === "google") {
      setConfirming(true);
      try {
        const uploaded = await Promise.all(
          picks.map((u) => uploadExternalUrlToGhl(u?.url))
        );
        const ok = uploaded.filter(Boolean);
        if (!ok.length) {
          toast.error("Could not upload selected Google image(s) to GHL");
          return;
        }
        if (ok.length < picks.length) {
          toast.error(`${picks.length - ok.length} image(s) failed to upload`);
        }
        if (multiSelect) onSelect && onSelect(ok);
        else onSelect && onSelect(ok[0]);
        onClose && onClose();
      } finally {
        setConfirming(false);
      }
      return;
    }

    if (multiSelect) onSelect && onSelect(picks);
    else onSelect && onSelect(picks[0]);
    onClose && onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current && fileInputRef.current.click();
  };

  const processFiles = async (files) => {
    if (!files.length || !agency?._id) return;

    if (enableCrop) {
      const file = files[0];
      if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
      setCropFile(file);
      setCropImageUrl(URL.createObjectURL(file));
      setCropOpen(true);
      return;
    }

    setUploading(true);
    try {
      const results = await Promise.all(
        files.map((file) => uploadImageToGhl(file, agency))
      );
      const successCount = results.filter((r) => r?.success).length;
      const failed = results.length - successCount;
      if (successCount) {
        toast.success(
          `${successCount} image${successCount > 1 ? "s" : ""} uploaded`
        );
        await fetchMedia();
      }
      if (failed) {
        toast.error(`${failed} upload${failed > 1 ? "s" : ""} failed`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await processFiles(files);
  };

  const handleDragEnter = (e) => {
    if (!showUpload) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      dragCounter.current += 1;
      setIsDragging(true);
    }
  };

  const handleDragOver = (e) => {
    if (!showUpload) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    if (!showUpload) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDrop = async (e) => {
    if (!showUpload) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    const isAccepted = (f) => {
      if (!accept || accept === "*" || accept === "*/*") return true;
      if (accept === "image/*") return f.type?.startsWith("image/");
      return accept
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .some((a) => {
          if (a.endsWith("/*")) return f.type?.startsWith(a.slice(0, -1));
          if (a.startsWith(".")) return f.name?.toLowerCase().endsWith(a);
          return f.type?.toLowerCase() === a;
        });
    };
    const files = dropped.filter(isAccepted);
    if (!files.length) {
      toast.error("Please drop valid image files");
      return;
    }
    await processFiles(files);
  };

  const closeCropper = () => {
    setCropOpen(false);
    setCropFile(null);
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
      setCropImageUrl(null);
    }
  };

  const handleCropComplete = async (blob) => {
    if (!blob) {
      closeCropper();
      return;
    }
    setUploading(true);
    try {
      const outputType = blob.type || "image/jpeg";
      const baseName = (cropFile?.name || "cropped-image").replace(/\.[^./\\]+$/, "");
      const extension = outputType === "image/png" ? "png" : "jpg";
      const file = new File([blob], `${baseName}.${extension}`, { type: outputType });
      const result = await uploadImageToGhl(file, agency);
      if (result?.success) {
        toast.success("Image uploaded");
        await fetchMedia();
      } else {
        toast.error(result?.data || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      closeCropper();
    }
  };

  const openItemMenu = (e, item) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItem(item);
  };

  const closeItemMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const handleGetLink = async () => {
    const url = menuItem?.image;
    closeItemMenu();
    const ok = await copyToClipboard(url);
    if (ok) toast.success("Link copied to clipboard");
    else toast.error("Could not copy link");
  };

  const handlePreview = (itemOverride) => {
    const target = itemOverride || menuItem;
    const url = target?.image;
    closeItemMenu();
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (item) => {
    const url = item?.image;
    if (!url) return;
    const filename = item?.name || `${deriveName(item)}.jpg`;
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
    } catch (e) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(15, 23, 42, 0.45)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          width: "100%",
          maxWidth: "1400px",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.25)",
          position: "relative",
        },
      }}
    >
      {isDragging && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            backgroundColor: "rgba(239, 246, 255, 0.92)",
            border: "3px dashed #2563eb",
            borderRadius: "16px",
            pointerEvents: "none",
            backdropFilter: "blur(2px)",
            color: "#1d4ed8",
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 56 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
            Drop image{enableCrop ? "" : "s"} to upload
          </Typography>
          <Typography variant="body2" sx={{ color: "#1e40af", opacity: 0.8 }}>
            {enableCrop
              ? "You'll be able to crop before saving"
              : `Files will be added to ${title}`}
          </Typography>
        </Box>
      )}
      <DialogTitle sx={{ padding: "28px 36px 16px 36px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              fontSize: "1.5rem",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
          <Box display="flex" alignItems="center" gap={1.2}>
            {showUpload && source === "ghl" && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={accept}
                  multiple
                  onChange={handleFileChange}
                />
                <Button
                  onClick={handleUploadClick}
                  disabled={uploading}
                  startIcon={
                    uploading ? (
                      <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                    ) : (
                      <FileUploadOutlinedIcon />
                    )
                  }
                  variant="contained"
                  sx={{
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                    boxShadow: "none",
                    padding: "8px 18px",
                    "&:hover": { backgroundColor: "#1d4ed8", boxShadow: "none" },
                    "&.Mui-disabled": { backgroundColor: "#93c5fd", color: "#ffffff" },
                  }}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "8px 36px 24px 36px" }}>
        {(() => {
          const FIELD_HEIGHT = 44;
          const fieldSx = {
            height: `${FIELD_HEIGHT}px`,
            "& .MuiOutlinedInput-root": {
              height: `${FIELD_HEIGHT}px`,
              minHeight: `${FIELD_HEIGHT}px`,
              borderRadius: "10px",
              backgroundColor: "#ffffff",
              fontSize: "14px",
              color: "#0f172a",
              padding: 0,
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#cbd5e1",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#2563eb",
                borderWidth: "1.5px",
              },
            },
            "& .MuiOutlinedInput-input": {
              height: `${FIELD_HEIGHT}px`,
              boxSizing: "border-box",
              padding: "0 14px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiSelect-select": {
              height: `${FIELD_HEIGHT}px !important`,
              minHeight: `${FIELD_HEIGHT}px !important`,
              boxSizing: "border-box",
              padding: "0 36px 0 14px !important",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiSelect-icon": {
              color: "#64748b",
              right: 10,
              fontSize: 22,
            },
            "& input::placeholder": { color: "#94a3b8", opacity: 1 },
          };
          return (
            <Box
              sx={{
                display: "flex !important",
                flexDirection: "row",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
                width: "100%",
                marginBottom: "20px",
              }}
            >
              {showSearch && (
                <TextField
                  size="small"
                  placeholder="Search media or explore stock images."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    ...fieldSx,
                    width: "340px",
                    flexShrink: 0,
                    marginLeft: "auto",
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      paddingLeft: "12px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ marginRight: "4px" }}>
                        <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {showSort && (
                <TextField
                  select
                  size="small"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  sx={{
                    ...fieldSx,
                    width: "220px",
                    flexShrink: 0,
                    marginLeft: !showSearch ? "auto" : 0,
                  }}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownIcon,
                    MenuProps: {
                      PaperProps: {
                        sx: { borderRadius: "10px", marginTop: "4px" },
                      },
                    },
                  }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {showFilter && filterOptions?.length > 0 && (
                <TextField
                  select
                  size="small"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  sx={{
                    ...fieldSx,
                    width: "130px",
                    flexShrink: 0,
                    marginLeft: !showSort ? "auto" : 0,
                  }}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownIcon,
                    MenuProps: {
                      PaperProps: {
                        sx: { borderRadius: "10px", marginTop: "4px" },
                      },
                    },
                  }}
                >
                  {filterOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {showViewToggle && (
                <ToggleButtonGroup
                  exclusive
                  value={view}
                  onChange={(_, v) => v && setView(v)}
                  sx={{
                    height: `${FIELD_HEIGHT}px`,
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    flexShrink: 0,
                    overflow: "hidden",
                    marginLeft: !showSort && !showFilter ? "auto" : 0,
                    "& .MuiToggleButton-root": {
                      height: `${FIELD_HEIGHT}px`,
                      width: `${FIELD_HEIGHT}px`,
                      border: "1px solid #e2e8f0",
                      padding: 0,
                      color: "#64748b",
                      "&:hover": { backgroundColor: "#f8fafc" },
                      "&.Mui-selected": {
                        backgroundColor: "#f1f5f9",
                        color: "#0f172a",
                        "&:hover": { backgroundColor: "#e2e8f0" },
                      },
                    },
                  }}
                >
                  <ToggleButton value="grid">
                    <GridViewRoundedIcon sx={{ fontSize: 20 }} />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <FormatListBulletedRoundedIcon sx={{ fontSize: 20 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          );
        })()}

        {googleEnabled && (
          <Box
            sx={{
              display: "inline-flex",
              padding: "4px",
              borderRadius: "12px",
              backgroundColor: "#f1f5f9",
              border: "1px solid #e2e8f0",
              marginBottom: "16px",
              gap: "4px",
            }}
          >
            {[
              { value: "ghl", label: "My Media", Icon: PermMediaOutlinedIcon },
              { value: "google", label: "Google Images", Icon: FaGoogle },
            ].map(({ value, label, Icon }) => {
              const active = source === value;
              return (
                <Box
                  key={value}
                  onClick={() => setSource(value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 14px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    color: active ? "#0f172a" : "#64748b",
                    backgroundColor: active ? "#ffffff" : "transparent",
                    boxShadow: active
                      ? "0 1px 3px rgba(15,23,42,0.08)"
                      : "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      color: active ? "#0f172a" : "#0f172a",
                      backgroundColor: active ? "#ffffff" : "rgba(255,255,255,0.6)",
                    },
                  }}
                >
                  <Icon
                    style={{
                      fontSize: 16,
                      color: active
                        ? value === "google"
                          ? "#ea4335"
                          : "#2563eb"
                        : "#94a3b8",
                    }}
                  />
                  {label}
                </Box>
              );
            })}
          </Box>
        )}

        {showFilesLabel && (
          <Typography
            variant="body2"
            sx={{
              color: "#2563eb",
              fontWeight: 600,
              marginBottom: "14px",
              fontSize: "13px",
              letterSpacing: "0.01em",
            }}
          >
            {source === "google" ? "Google Place Images" : "Files"}
          </Typography>
        )}

        {(source === "ghl" ? loading : googleLoading) ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="40vh"
            gap={1.5}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" sx={{ color: "#888" }}>
              {source === "google" ? "Loading Google images..." : "Loading media..."}
            </Typography>
          </Box>
        ) : totalItems === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="40vh"
            gap={1}
            sx={{ color: "#888" }}
          >
            <ImageNotSupportedIcon sx={{ fontSize: 48, color: "#bbb" }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {source === "google"
                ? googleError
                  ? "Could not load Google images"
                  : "No Google images found"
                : "No media found"}
            </Typography>
            <Typography variant="body2">
              {search
                ? "Try a different search term."
                : source === "google"
                ? googleError ||
                  "This business has no Google Place photos yet."
                : "Upload media to see it here."}
            </Typography>
          </Box>
        ) : view === "list" ? (
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            {/* Table header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "48px 80px minmax(220px, 1.6fr) 1fr 1fr 1fr 60px",
                alignItems: "center",
                padding: "12px 16px",
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
                color: "#64748b",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              <Box>
                <Checkbox
                  size="small"
                  disabled
                  sx={{ padding: 0, "&.Mui-disabled": { color: "#cbd5e1" } }}
                />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Media
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Name
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Updated On
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                File Size
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                Dimensions
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 500, textAlign: "center" }}
              >
                Actions
              </Typography>
            </Box>

            {paginated.map((item, idx) => {
              const url = item?.image;
              const name = deriveName(item, idx);
              const selected = isSelected(url);
              const orderIdx = multiSelect ? selectedImages.findIndex((item) => item.url === url) : -1;
              const dim = dimensions[url];
              return (
                <Box
                  key={`${url}-${idx}`}
                  onClick={() => toggleSelection(url,item?._id)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "48px 80px minmax(220px, 1.6fr) 1fr 1fr 1fr 60px",
                    alignItems: "center",
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                    backgroundColor: selected ? "#eff6ff" : "#ffffff",
                    "&:hover": {
                      backgroundColor: selected ? "#dbeafe" : "#f8fafc",
                    },
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Box>
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => toggleSelection(url,item?._id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        padding: 0,
                        color: "#cbd5e1",
                        "&.Mui-checked": { color: "#2563eb" },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#f1f5f9",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={url}
                      alt={name}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    minWidth={0}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#2563eb",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Typography>
                    {multiSelect && selected && orderIdx >= 0 && (
                      <Box
                        sx={{
                          minWidth: 22,
                          height: 22,
                          padding: "0 6px",
                          borderRadius: "11px",
                          backgroundColor: "#2563eb",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {orderIdx + 1}
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    {formatDate(item?.dateUpdated || item?.dateAdded)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    {formatSize(item?.size)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    {dim ? `${dim.w} × ${dim.h} PX` : "—"}
                  </Typography>
                  <Box display="flex" justifyContent="center">
                    <IconButton
                      size="small"
                      onClick={(e) => openItemMenu(e, item)}
                      sx={{ color: "#64748b" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "18px",
              paddingBottom: "4px",
            }}
          >
            {paginated.map((item, idx) => {
              const url = item?.image;
              const name = deriveName(item, idx);
              const selected = isSelected(url);
              const orderIdx = multiSelect ? selectedImages.findIndex((item) => item.url === url) : -1;
              return (
                <Box
                  key={`${url}-${idx}`}
                  onClick={() => toggleSelection(url, item?._id)}
                  sx={{
                    position: "relative",
                    aspectRatio: "1/1",
                    borderRadius: "12px",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: selected
                      ? "3px solid #2563eb"
                      : "1px solid #e5e7eb",
                    backgroundColor: "#ffffff",
                    boxShadow: selected
                      ? "0 6px 18px rgba(37, 99, 235, 0.25)"
                      : "0 1px 3px rgba(15, 23, 42, 0.06)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(15,23,42,0.12)",
                    },
                    "&:hover .tile-actions": { opacity: 1, transform: "translateY(0)" },
                    "&:hover .tile-gradient": { opacity: 1 },
                    "&:hover .tile-extra-info": {
                      opacity: 1,
                      maxHeight: "60px",
                      marginTop: "6px",
                    },
                  }}
                >
                  <img
                    src={url}
                    alt={name}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  <Box
                    className="tile-gradient"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0) 65%)",
                      pointerEvents: "none",
                      opacity: 0.85,
                      transition: "opacity 0.2s ease",
                    }}
                  />

                  {/* Hover actions: download + 3-dot */}
                  <Box
                    className="tile-actions"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 0.5,
                      opacity: 0,
                      transform: "translateY(-4px)",
                      transition: "opacity 0.2s ease, transform 0.2s ease",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(item);
                      }}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        color: "#0f172a",
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                        "&:hover": { backgroundColor: "#ffffff" },
                      }}
                    >
                      <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => openItemMenu(e, item)}
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        color: "#0f172a",
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                        "&:hover": { backgroundColor: "#ffffff" },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>

                  {/* Bottom info panel (filename always, details on hover) */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 12,
                      right: 12,
                      bottom: 10,
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#ffffff",
                          fontWeight: 600,
                          fontSize: "12.5px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                        }}
                      >
                        {name}
                      </Typography>
                      <OpenInFullIcon
                        sx={{
                          color: "#ffffff",
                          fontSize: 14,
                          opacity: 0.85,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                    <Box
                      className="tile-extra-info"
                      sx={{
                        opacity: 0,
                        maxHeight: 0,
                        marginTop: 0,
                        overflow: "hidden",
                        transition:
                          "opacity 0.2s ease, max-height 0.2s ease, margin-top 0.2s ease",
                      }}
                    >
                      {(item?.dateUpdated || item?.dateAdded) && (
                        <Typography
                          sx={{
                            display: "block",
                            color: "rgba(255,255,255,0.9)",
                            fontSize: "11.5px",
                            lineHeight: 1.35,
                            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                          }}
                        >
                          {formatRelativeDate(item?.dateUpdated || item?.dateAdded)}
                        </Typography>
                      )}
                      {(item?.size || item?.size === 0) && (
                        <Typography
                          sx={{
                            display: "block",
                            color: "rgba(255,255,255,0.9)",
                            fontSize: "11.5px",
                            lineHeight: 1.35,
                            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                          }}
                        >
                          {formatSize(item?.size)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {selected && !multiSelect && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        backgroundColor: "#ffffff",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      }}
                    >
                      <CheckCircleIcon sx={{ color: "#2563eb", fontSize: 28 }} />
                    </Box>
                  )}
                  {multiSelect && selected && orderIdx >= 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        minWidth: "24px",
                        height: "24px",
                        padding: "0 6px",
                        borderRadius: "12px",
                        backgroundColor: "#2563eb",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      }}
                    >
                      {orderIdx + 1}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <Box
        sx={{
          padding: "16px 36px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          backgroundColor: "#fafafa",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ color: "#64748b", minWidth: "120px" }}>
          {loading
            ? "..."
            : `${totalItems} item${totalItems !== 1 ? "s" : ""}${
                search ? " (filtered)" : ""
              }`}
        </Typography>

        {!loading && totalItems > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "2px 4px",
            }}
          >
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              sx={{ borderRadius: "8px" }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            {Array.from({ length: totalPages }).map((_, i) => {
              const pageNum = i + 1;
              const isActive = pageNum === safePage;
              const showPage =
                totalPages <= 7 ||
                pageNum === 1 ||
                pageNum === totalPages ||
                Math.abs(pageNum - safePage) <= 1;
              const showEllipsis =
                !showPage &&
                ((pageNum === safePage - 2 && safePage > 3) ||
                  (pageNum === safePage + 2 && safePage < totalPages - 2));

              if (showEllipsis) {
                return (
                  <Typography
                    key={`ellipsis-${pageNum}`}
                    variant="body2"
                    sx={{ color: "#999", padding: "0 4px" }}
                  >
                    ...
                  </Typography>
                );
              }
              if (!showPage) return null;

              return (
                <Box
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  sx={{
                    minWidth: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "#ffffff" : "#444",
                    backgroundColor: isActive ? "#2563eb" : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: isActive ? "#2563eb" : "#f0f0f0",
                    },
                  }}
                >
                  {pageNum}
                </Box>
              );
            })}

            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              sx={{ borderRadius: "8px" }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box display="flex" gap={1}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: "#ccc",
              color: "#333",
              "&:hover": { borderColor: "#999", backgroundColor: "#f0f0f0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectionCount === 0 || confirming}
            startIcon={
              confirming ? (
                <CircularProgress size={14} sx={{ color: "#ffffff" }} />
              ) : null
            }
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#0f172a",
              color: "#ffffff",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1e293b", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "#cbd5e1", color: "#ffffff" },
            }}
          >
            {confirming
              ? "Uploading..."
              : multiSelect && selectionCount > 0
              ? `${confirmLabel} (${selectionCount})`
              : confirmLabel}
          </Button>
        </Box>
      </Box>

      {enableCrop && (
        <ReactCropModal
          open={cropOpen}
          onClose={closeCropper}
          currentImage={cropImageUrl}
          onCropComplete={handleCropComplete}
          aspectRatio={cropAspectRatio}
        />
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeItemMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            border: "1px solid #e5e7eb",
            minWidth: "160px",
          },
        }}
      >
        <MenuItem
          onClick={() => handlePreview()}
          sx={{
            display: "flex",
            gap: 1.2,
            fontSize: "14px",
            color: "#0f172a",
            "&:hover": { backgroundColor: "#f1f5f9" },
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />
          Preview
        </MenuItem>
        <MenuItem
          onClick={handleGetLink}
          sx={{
            display: "flex",
            gap: 1.2,
            fontSize: "14px",
            color: "#0f172a",
            "&:hover": { backgroundColor: "#f1f5f9" },
          }}
        >
          <LinkIcon fontSize="small" sx={{ color: "#2563eb" }} />
          Get Link
        </MenuItem>
      </Menu>
    </Dialog>
  );
}

export default GhlMediaGallery;
